import AtAGlance from "../at-a-glance";
import imageComingSoon from "../../../assets/image-coming-soon.png";
import { HotelLocation } from "../../icons/icons";

/**
 * Editor preview for the "Dynamic At a glance (M&E)" layout.
 *
 * Extends AtAGlance rather than duplicating it: the hotel search, the Configure
 * modal, the media-library hooks, the offline-fallback section and the save
 * shape are all identical and stay in one place. Only the in-editor preview
 * differs, so only _renderConfigured() is overridden.
 *
 * The markup mirrors atAGlanceME.js (the slide renderer) class for class, so
 * the editor and the published slide pick up the same CSS and the author sees
 * what they will get. The five switchable panels are deliberately NOT drawn
 * here - the editor shows the slide's default (hero) state, and rendering five
 * hidden panels per block would bloat an 80-slide deck's DOM for no gain.
 */
export default class AtAGlanceME extends AtAGlance {
  static get toolbox() {
    return {
      ...AtAGlance.toolbox,
      title: "At A Glance (M&E)",
    };
  }

  /**
   * The six nav buttons, drawn flat so the author sees the real control row.
   * Inert in the editor: panel switching belongs to the published slide, and a
   * click here would fight the editor's own selection handling.
   */
  _renderNav() {
    const nav = document.createElement("nav");
    nav.classList.add("aag-me-nav");

    [
      ["Location<br/>map", AtAGlanceME.ICONS.map],
      ["Capacity<br/>chart", AtAGlanceME.ICONS.capacity],
      ["M&amp;E<br/>features", AtAGlanceME.ICONS.features],
      ["M&amp;E<br/>gallery", AtAGlanceME.ICONS.gallery],
      ["Food &amp;<br/>beverage", AtAGlanceME.ICONS.dining],
      ["Key<br/>features", AtAGlanceME.ICONS.keyfeatures],
    ].forEach(([label, icon]) => {
      const btn = document.createElement("span");
      btn.classList.add("aag-me-nav-btn");
      btn.innerHTML =
        `<span class="aag-me-nav-icon">${icon}</span>` +
        `<span class="aag-me-nav-label">${label}</span>`;
      nav.appendChild(btn);
    });

    return nav;
  }

  /**
   * Reads the room / meeting figures straight off the stored API response.
   *
   * The base block drives its capacity row from `selectedItems` (what the
   * author ticked in Configure). The M&E design has a fixed stat row instead -
   * guest rooms, meeting rooms, max capacity, breakfast - so it reads the
   * source rather than the selection, exactly as the slide renderer does.
   */
  _renderStats() {
    const api = this._data.apiData || {};
    const roomContent = api?.roomInfo?.collection?.[0]?.content || {};

    const stats = [
      [roomContent?.numOfBedrooms, "Guest<br/>rooms"],
      [roomContent?.numOfMeetingRooms, "Meeting<br/>rooms"],
      [roomContent?.maxCapacity, "Max<br/>cap"],
    ].filter(([v]) => v != null && String(v).trim() !== "");

    const hotelInfo = api?.hotel || api?.hotelInfo?.collection?.[0];
    const service = hotelInfo?.content?.service;
    const serviceList = Array.isArray(service?.values)
      ? service.values
      : service
        ? [service]
        : [];
    const hasBreakfast = serviceList.some((s) =>
      /breakfast/i.test(((s && s.service) || s)?.description || ""),
    );

    if (!stats.length && !hasBreakfast) return null;

    const row = document.createElement("div");
    row.classList.add("hotel-capacity-container", "aag-me-stats");

    stats.forEach(([value, label]) => {
      const stat = document.createElement("div");
      stat.classList.add("hotel-capacity-group", "aag-me-stat");
      stat.innerHTML =
        `<span class="hotel-capacity-value aag-me-stat-value">${value}</span>` +
        `<span class="hotel-capacity-label aag-me-stat-label">${label}</span>`;
      row.appendChild(stat);
    });

    if (hasBreakfast) {
      const stat = document.createElement("div");
      stat.classList.add("hotel-capacity-group", "aag-me-stat");
      stat.innerHTML =
        '<span class="hotel-capacity-value aag-me-stat-value aag-me-tick">&#10004;</span>' +
        '<span class="hotel-capacity-label aag-me-stat-label">Breakfast<br/>buffet</span>';
      row.appendChild(stat);
    }

    return row;
  }

  _renderDistances() {
    const d = this._data.distances || {};
    const rows = [
      ["Distance to airport", d.airport],
      ["Distance to train station", d.train],
      ["Distance to motorway", d.motorway],
    ].filter(([, v]) => v != null && String(v).trim() !== "");

    if (!rows.length) return null;

    const dl = document.createElement("dl");
    dl.classList.add("aag-me-distances");
    rows.forEach(([label, value]) => {
      const row = document.createElement("div");
      row.classList.add("aag-me-distance");
      row.innerHTML = `<dt>${label}</dt><dd>${value}</dd>`;
      dl.appendChild(row);
    });
    return dl;
  }

  /**
   * {@inheritdoc}
   */
  _renderConfigured() {
    if (this._contentEl) {
      this._contentEl.remove();
    }

    this._contentEl = document.createElement("div");
    this._contentEl.classList.add("at-a-glance-content-container", "aag-me");
    // The published slide opens on the cover; the editor opens on the detail
    // view, because that is the state the author is configuring.
    this._contentEl.setAttribute("data-panel", "hero");

    const hotel = this._getHotelDetails();

    if (!hotel && this._data.selectedItems.length === 0) {
      const placeholder = document.createElement("div");
      placeholder.style.cssText = "color:#999;font-size:16px;";
      placeholder.textContent = 'Click "Configure" to set up At A Glance (M&E)';
      this._contentEl.appendChild(placeholder);
      this._container.appendChild(this._contentEl);
      return;
    }

    /* ── left: hero + overlay ───────────────────────────────────────────── */
    const leftCol = document.createElement("div");
    leftCol.classList.add("at-a-glance-left");

    if (hotel?.heroImage) {
      const heroImg = document.createElement("img");
      heroImg.src = hotel.heroImage;
      heroImg.classList.add("at-a-glance-hero");
      heroImg.style.opacity = this._data.heroOpacity;
      leftCol.appendChild(heroImg);

      if (!this.readOnly && this.config?.showMediaLibrary) {
        leftCol.appendChild(this._createReplaceBtn("hero"));
        leftCol.appendChild(this._createResetBtn("hero"));
      }
    }

    if (hotel) {
      const overlay = document.createElement("div");
      overlay.classList.add("aag-me-hero-overlay");

      if (hotel.brandCode && this.config?.brandLogos?.[hotel.brandCode]) {
        const logoEl = document.createElement("div");
        logoEl.classList.add("hotel-logo-container");
        logoEl.innerHTML = this.config.brandLogos[hotel.brandCode];
        overlay.appendChild(logoEl);
      }

      // Same contentEditable wiring as the base block, so the title and
      // description stay editable in place and keep saving to the same fields.
      const titleEl = document.createElement("h3");
      titleEl.classList.add("aag-me-hero-title");
      titleEl.innerHTML = hotel.title || "";
      if (!this.readOnly) {
        titleEl.contentEditable = "true";
        titleEl.dataset.placeholder = "Overlay tagline";
        titleEl.addEventListener("keydown", this._handleEditableEnter);
        titleEl.addEventListener("input", () => {
          this._data.title = titleEl.innerHTML;
        });
      }
      overlay.appendChild(titleEl);

      const descEl = document.createElement("p");
      descEl.classList.add("aag-me-hero-body");
      descEl.innerHTML = hotel.description || "";
      if (!this.readOnly) {
        descEl.contentEditable = "true";
        descEl.dataset.placeholder = "Overlay description";
        descEl.addEventListener("keydown", this._handleEditableEnter);
        descEl.addEventListener("input", () => {
          this._data.description = descEl.innerHTML;
        });
      }
      overlay.appendChild(descEl);

      leftCol.appendChild(overlay);
    }

    this._contentEl.appendChild(leftCol);

    /* ── right: two images, stats, place, distances, nav ─────────────────── */
    const rightCol = document.createElement("div");
    rightCol.classList.add("at-a-glance-right");

    if (hotel) {
      const canEditImages = !this.readOnly && this.config?.showMediaLibrary;
      const grid = document.createElement("div");
      grid.classList.add("aag-me-images");

      // Two slots, not the base block's four: the M&E design shows a guest
      // room and a meeting room. Empty slots still render in edit mode so the
      // author has somewhere to drop an image.
      ["guestRoom", "meetingRoom"].forEach((slot) => {
        const url = hotel.images?.[slot];
        if (!url && !canEditImages) return;

        const container = document.createElement("div");
        container.classList.add("aag-me-img");

        const img = document.createElement("img");
        if (url) {
          img.src = url;
        } else {
          container.classList.add("aag-me-img-empty");
          img.src = imageComingSoon;
        }
        img.alt = slot;
        container.appendChild(img);

        if (canEditImages) {
          container.appendChild(this._createReplaceBtn(slot));
          container.appendChild(this._createResetBtn(slot));
        }
        grid.appendChild(container);
      });

      if (grid.childElementCount) {
        rightCol.appendChild(grid);
      }

      const stats = this._renderStats();
      if (stats) rightCol.appendChild(stats);

      const place = document.createElement("div");
      place.classList.add("hotel-heading-container", "aag-me-place");
      const pin = document.createElement("div");
      pin.classList.add("location-pin-icon", "aag-me-pin");
      pin.innerHTML = HotelLocation;
      place.appendChild(pin);
      if (hotel.country) {
        const el = document.createElement("p");
        el.classList.add("hotel-country", "aag-me-country");
        el.textContent = hotel.country;
        place.appendChild(el);
      }
      if (hotel.city) {
        const el = document.createElement("h3");
        el.classList.add("hotel-city", "aag-me-city");
        el.textContent = hotel.city;
        place.appendChild(el);
      }
      if (hotel.name) {
        const el = document.createElement("p");
        el.classList.add("hotel-name", "aag-me-hotel");
        el.textContent = hotel.name;
        place.appendChild(el);
      }
      if (hotel.url) {
        const link = document.createElement("a");
        link.classList.add("aag-me-website");
        link.textContent = "Hotel website";
        link.href = "https://www.radissonhotels.com" + hotel.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        place.appendChild(link);
      }
      if (hotel.location) {
        const row = document.createElement("p");
        row.classList.add("hotel-footer-location-container", "aag-me-location");
        row.innerHTML =
          '<span class="hotel-footer-location-label aag-me-location-label">Location:</span>' +
          `<span class="hotel-footer-location aag-me-location-value">${hotel.location}</span>`;
        place.appendChild(row);
      }
      // The pin is always present, so count the rest before appending.
      if (place.childElementCount > 1) rightCol.appendChild(place);

      // Distances moved onto the location map as a toggled overlay. The
      // editor preview does not render the map panel, so they are configured
      // here but shown only on the published slide.
      rightCol.appendChild(this._renderNav());
    }

    this._contentEl.appendChild(rightCol);
    this._container.appendChild(this._contentEl);
  }
}

/**
 * Icon set, kept identical to the slide renderer's so the two agree.
 */
AtAGlanceME.ICONS = {
  map: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"/><circle cx="12" cy="10" r="2.6"/></svg>',
  capacity:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="7" cy="9" r="2.4"/><circle cx="12" cy="7.5" r="2.4"/><circle cx="17" cy="9" r="2.4"/><path d="M3 17c0-2.2 1.8-3.6 4-3.6s4 1.4 4 3.6"/><path d="M13 17c0-2.2 1.8-3.6 4-3.6s4 1.4 4 3.6"/></svg>',
  features:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="5" y="3.5" width="14" height="17" rx="2"/><path d="M9 3.5h6v3H9z"/><path d="M8.5 11h7M8.5 15h4"/></svg>',
  gallery:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3.5" y="5" width="17" height="14" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="m4.5 17 4.5-4.5 3.5 3.5 3-2.5 4 3.5"/></svg>',
  dining:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="8.5"/><path d="M12 3.5v17M8 3.9c2 2.6 2 13.6 0 16.2"/></svg>',
  keyfeatures:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="3.2"/><path d="M19.4 14a1.5 1.5 0 0 0 .3 1.7l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-1.7-.3 1.5 1.5 0 0 0-.9 1.4v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-1-1.4 1.5 1.5 0 0 0-1.7.3l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0 .3-1.7 1.5 1.5 0 0 0-1.4-.9h-.2a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1.4-1 1.5 1.5 0 0 0-.3-1.7l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.7.3H10a1.5 1.5 0 0 0 .9-1.4v-.2a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 .9 1.4 1.5 1.5 0 0 0 1.7-.3l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0-.3 1.7V10a1.5 1.5 0 0 0 1.4.9h.2a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9Z"/></svg>',
  website:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17"/><path d="M12 3.5c2.4 2.6 2.4 14.4 0 17M12 3.5c-2.4 2.6-2.4 14.4 0 17"/></svg>',
};
